import React, { useCallback, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import chroma from "chroma-js";
import { HexColorInput, HexColorPicker } from "react-colorful";
import { darkOrLightColor } from "../../../helpers/darkOrLightColor";
import {
  mix_colors_wrapper,
  tool_heading,
  mix_color_title,
  subtitle,
  trust_signals,
  mix_tool_wrapper,
  section_card,
  panel_heading,
  mix_color_picker_wrapper,
  color_picker_wrapper,
  color_label,
  color_preview,
  react_colorful,
  input_hex_color,
  mix_settings,
  field_group,
  mix_mode_select,
  ratio_header,
  ratio_values,
  ratio_slider,
  control_actions,
  swap_btn,
  action_btn,
  result_card,
  mix_color_result,
  result_hex,
  color_formats_list,
  format_button,
  format_label,
  format_value,
  result_actions,
  primary_action,
  scale_section,
  gradient_bar,
  scale_preview,
  scale_color,
  toast,
} from "../../../styles/modules/mix_colors.module.css";

const TEXTS = {
  es: {
    title: "Mezclar colores online",
    subtitle:
      "Combina dos colores, ajusta la proporción y obtén el resultado en HEX, RGB, HSL y CMYK.",
    instant: "Resultado instantáneo",
    modes: "5 modos de mezcla",
    free: "Gratis",
    colorsToCombine: "Elige los colores",
    firstColor: "Color 1",
    secondColor: "Color 2",
    colorScale: "Escala entre ambos colores",
    mixResult: "Resultado de la mezcla",
    mixMode: "Modelo de color",
    ratio: "Proporción de la mezcla",
    swap: "Intercambiar colores",
    copyResult: "Copiar resultado",
    copyScale: "Copiar escala HEX",
    copyCSS: "Copiar variables CSS",
    randomColors: "Colores aleatorios",
    download: "Descargar mezcla y escala",
    downloaded: "Imagen descargada",
    copied: "Copiado",
    copyError: "No se pudo copiar.",
    downloadError: "No se pudo crear la descarga.",
    copyFormat: "Copiar valor",
    paletteTitle: "Mezcla de colores",
  },
  en: {
    title: "Mix colors online",
    subtitle:
      "Combine two colors, adjust their ratio, and get the result in HEX, RGB, HSL, and CMYK.",
    instant: "Instant result",
    modes: "5 mixing modes",
    free: "Free",
    colorsToCombine: "Choose colors",
    firstColor: "Color 1",
    secondColor: "Color 2",
    colorScale: "Scale between both colors",
    mixResult: "Mix result",
    mixMode: "Color model",
    ratio: "Mix ratio",
    swap: "Swap colors",
    copyResult: "Copy result",
    copyScale: "Copy HEX scale",
    copyCSS: "Copy CSS variables",
    randomColors: "Random colors",
    download: "Download mix and scale",
    downloaded: "Image downloaded",
    copied: "Copied",
    copyError: "Could not copy.",
    downloadError: "Could not create the download.",
    copyFormat: "Copy value",
    paletteTitle: "Color mix",
  },
  pt: {
    title: "Misturar cores online",
    subtitle:
      "Combine duas cores, ajuste a proporção e obtenha o resultado em HEX, RGB, HSL e CMYK.",
    instant: "Resultado instantâneo",
    modes: "5 modos de mistura",
    free: "Grátis",
    colorsToCombine: "Escolha as cores",
    firstColor: "Cor 1",
    secondColor: "Cor 2",
    colorScale: "Escala entre as duas cores",
    mixResult: "Resultado da mistura",
    mixMode: "Modelo de cor",
    ratio: "Proporção da mistura",
    swap: "Trocar cores",
    copyResult: "Copiar resultado",
    copyScale: "Copiar escala HEX",
    copyCSS: "Copiar variáveis CSS",
    randomColors: "Cores aleatórias",
    download: "Baixar mistura e escala",
    downloaded: "Imagem baixada",
    copied: "Copiado",
    copyError: "Não foi possível copiar.",
    downloadError: "Não foi possível criar o download.",
    copyFormat: "Copiar valor",
    paletteTitle: "Mistura de cores",
  },
  fr: {
    title: "Mélanger des couleurs en ligne",
    subtitle:
      "Combinez deux couleurs, ajustez leur proportion et obtenez le résultat en HEX, RGB, HSL et CMYK.",
    instant: "Résultat instantané",
    modes: "5 modes de mélange",
    free: "Gratuit",
    colorsToCombine: "Choisissez les couleurs",
    firstColor: "Couleur 1",
    secondColor: "Couleur 2",
    colorScale: "Échelle entre les deux couleurs",
    mixResult: "Résultat du mélange",
    mixMode: "Modèle colorimétrique",
    ratio: "Proportion du mélange",
    swap: "Échanger les couleurs",
    copyResult: "Copier le résultat",
    copyScale: "Copier l’échelle HEX",
    copyCSS: "Copier les variables CSS",
    randomColors: "Couleurs aléatoires",
    download: "Télécharger le mélange",
    downloaded: "Image téléchargée",
    copied: "Copié",
    copyError: "Impossible de copier.",
    downloadError: "Impossible de créer le téléchargement.",
    copyFormat: "Copier la valeur",
    paletteTitle: "Mélange de couleurs",
  },
  de: {
    title: "Farben online mischen",
    subtitle:
      "Kombiniere zwei Farben, passe ihr Verhältnis an und erhalte das Ergebnis als HEX, RGB, HSL und CMYK.",
    instant: "Sofortiges Ergebnis",
    modes: "5 Mischmodi",
    free: "Kostenlos",
    colorsToCombine: "Farben auswählen",
    firstColor: "Farbe 1",
    secondColor: "Farbe 2",
    colorScale: "Skala zwischen beiden Farben",
    mixResult: "Mischergebnis",
    mixMode: "Farbmodell",
    ratio: "Mischverhältnis",
    swap: "Farben tauschen",
    copyResult: "Ergebnis kopieren",
    copyScale: "HEX-Skala kopieren",
    copyCSS: "CSS-Variablen kopieren",
    randomColors: "Zufällige Farben",
    download: "Mischung herunterladen",
    downloaded: "Bild heruntergeladen",
    copied: "Kopiert",
    copyError: "Kopieren fehlgeschlagen.",
    downloadError: "Download konnte nicht erstellt werden.",
    copyFormat: "Wert kopieren",
    paletteTitle: "Farbmischung",
  },
};

const MIX_MODES = ["rgb", "lab", "lch", "hsl", "lrgb"];

function rgbToCmyk(r, g, b) {
  let cyan = 1 - r / 255;
  let magenta = 1 - g / 255;
  let yellow = 1 - b / 255;
  const black = Math.min(cyan, magenta, yellow);
  cyan = (cyan - black) / (1 - black) || 0;
  magenta = (magenta - black) / (1 - black) || 0;
  yellow = (yellow - black) / (1 - black) || 0;
  return `cmyk(${Math.round(cyan * 100)}%, ${Math.round(
    magenta * 100
  )}%, ${Math.round(yellow * 100)}%, ${Math.round(black * 100)}%)`;
}

function MixColors({ locale }) {
  const texts = TEXTS[locale] || TEXTS.es;
  const [hexColor1, setHexColor1] = useState("#3B8CDE");
  const [hexColor2, setHexColor2] = useState("#DD00FF");
  const [mixMode, setMixMode] = useState("lch");
  const [mixAmount, setMixAmount] = useState(50);
  const [toastMessage, setToastMessage] = useState("");

  const colorResult = useMemo(
    () => chroma.mix(hexColor1, hexColor2, mixAmount / 100, mixMode),
    [hexColor1, hexColor2, mixAmount, mixMode]
  );
  const colorResultHex = colorResult.hex().toUpperCase();
  const colorScale = useMemo(
    () => chroma.scale([hexColor1, hexColor2]).mode(mixMode).colors(9),
    [hexColor1, hexColor2, mixMode]
  );
  const gradientCSS = `linear-gradient(90deg, ${hexColor1}, ${hexColor2})`;

  const displayToast = useCallback(message => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2200);
  }, []);

  const copyToClipboard = useCallback(
    async text => {
      try {
        await navigator.clipboard.writeText(text);
        displayToast(texts.copied);
      } catch (error) {
        displayToast(texts.copyError);
      }
    },
    [displayToast, texts.copied, texts.copyError]
  );

  const swapColors = () => {
    setHexColor1(hexColor2);
    setHexColor2(hexColor1);
    setMixAmount(currentAmount => 100 - currentAmount);
  };

  const randomColors = () => {
    setHexColor1(chroma.random().hex().toUpperCase());
    setHexColor2(chroma.random().hex().toUpperCase());
  };

  const colorFormats = [
    { label: "HEX", value: colorResultHex },
    { label: "RGB", value: colorResult.css("rgb") },
    { label: "HSL", value: colorResult.css("hsl") },
    { label: "CMYK", value: rgbToCmyk(...colorResult.rgb()) },
  ];

  const copyCSSVariables = () => {
    const css = `:root {\n  --mix-color-1: ${hexColor1.toUpperCase()};\n  --mix-color-2: ${hexColor2.toUpperCase()};\n  --mix-result: ${colorResultHex};\n${colorScale
      .map(
        (color, index) => `  --mix-step-${index + 1}: ${color.toUpperCase()};`
      )
      .join("\n")}\n}`;
    copyToClipboard(css);
  };

  const downloadMix = () => {
    try {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const width = 1200;
      const height = 700;
      const padding = 56;
      canvas.width = width;
      canvas.height = height;

      context.fillStyle = "#F8FAFC";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#1E293B";
      context.font = "700 34px Inter, Arial, sans-serif";
      context.fillText(texts.paletteTitle, padding, 58);
      context.textAlign = "right";
      context.fillStyle = "#64748B";
      context.font = "500 18px Inter, Arial, sans-serif";
      context.fillText("paletadecolores.online", width - padding, 55);

      const cardY = 100;
      const cardHeight = 250;
      const gap = 18;
      const cardWidth = (width - padding * 2 - gap * 2) / 3;
      const cards = [
        {
          color: hexColor1,
          label: `${texts.firstColor} · ${100 - mixAmount}%`,
        },
        {
          color: colorResultHex,
          label: `${texts.mixResult} · ${mixMode.toUpperCase()}`,
        },
        { color: hexColor2, label: `${texts.secondColor} · ${mixAmount}%` },
      ];

      cards.forEach((card, index) => {
        const x = padding + index * (cardWidth + gap);
        context.fillStyle = card.color;
        context.fillRect(x, cardY, cardWidth, cardHeight);
        context.textAlign = "center";
        context.fillStyle = darkOrLightColor(card.color);
        context.font = "700 22px Inter, Arial, sans-serif";
        context.fillText(
          card.color.toUpperCase(),
          x + cardWidth / 2,
          cardY + 205
        );
        context.font = "600 16px Inter, Arial, sans-serif";
        context.fillText(card.label, x + cardWidth / 2, cardY + 232);
      });

      const scaleY = 405;
      const swatchWidth = (width - padding * 2) / colorScale.length;
      colorScale.forEach((color, index) => {
        const normalizedColor = color.toUpperCase();
        const x = padding + index * swatchWidth;
        context.fillStyle = normalizedColor;
        context.fillRect(x, scaleY, swatchWidth + 1, 180);
        context.textAlign = "center";
        context.fillStyle = darkOrLightColor(normalizedColor);
        context.font = "700 15px Inter, Arial, sans-serif";
        context.fillText(normalizedColor, x + swatchWidth / 2, scaleY + 155);
      });

      canvas.toBlob(blob => {
        if (!blob) {
          displayToast(texts.downloadError);
          return;
        }
        saveAs(blob, "mezcla-de-colores.png");
        displayToast(texts.downloaded);
      }, "image/png");
    } catch (error) {
      displayToast(texts.downloadError);
    }
  };

  return (
    <section className={mix_colors_wrapper} aria-labelledby="mix-colors-title">
      <header className={tool_heading}>
        <h1 className={mix_color_title} id="mix-colors-title">
          {texts.title}
        </h1>
        <p className={subtitle}>{texts.subtitle}</p>
        <ul className={trust_signals} aria-label={texts.instant}>
          <li>{texts.instant}</li>
          <li>{texts.modes}</li>
          <li>{texts.free}</li>
        </ul>
      </header>

      <div className={mix_tool_wrapper}>
        <section className={section_card} aria-labelledby="mix-input-title">
          <h2 className={panel_heading} id="mix-input-title">
            {texts.colorsToCombine}
          </h2>

          <div className={mix_color_picker_wrapper}>
            {[
              {
                color: hexColor1,
                setColor: setHexColor1,
                label: texts.firstColor,
              },
              {
                color: hexColor2,
                setColor: setHexColor2,
                label: texts.secondColor,
              },
            ].map(item => (
              <div className={color_picker_wrapper} key={item.label}>
                <div className={color_label}>
                  <span>{item.label}</span>
                  <span
                    className={color_preview}
                    style={{ backgroundColor: item.color }}
                    aria-hidden="true"
                  />
                </div>
                <HexColorPicker
                  className={react_colorful}
                  color={item.color}
                  onChange={item.setColor}
                />
                <HexColorInput
                  className={input_hex_color}
                  color={item.color}
                  onChange={item.setColor}
                  prefixed
                  aria-label={`${item.label} HEX`}
                />
              </div>
            ))}
          </div>

          <div className={mix_settings}>
            <label className={field_group}>
              <span>{texts.mixMode}</span>
              <select
                className={mix_mode_select}
                value={mixMode}
                onChange={event => setMixMode(event.target.value)}
              >
                {MIX_MODES.map(mode => (
                  <option key={mode} value={mode}>
                    {mode.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>

            <div className={field_group}>
              <div className={ratio_header}>
                <span>{texts.ratio}</span>
                <span className={ratio_values}>
                  {100 - mixAmount}% / {mixAmount}%
                </span>
              </div>
              <input
                className={ratio_slider}
                type="range"
                min="0"
                max="100"
                value={mixAmount}
                onChange={event => setMixAmount(Number(event.target.value))}
                aria-label={texts.ratio}
              />
            </div>

            <div className={control_actions}>
              <button type="button" className={swap_btn} onClick={swapColors}>
                ↔ {texts.swap}
              </button>
              <button
                type="button"
                className={action_btn}
                onClick={randomColors}
              >
                {texts.randomColors}
              </button>
            </div>
          </div>
        </section>

        <section
          className={`${section_card} ${result_card}`}
          aria-labelledby="mix-result-title"
        >
          <h2 className={panel_heading} id="mix-result-title">
            {texts.mixResult}
          </h2>

          <button
            type="button"
            className={mix_color_result}
            style={{
              backgroundColor: colorResultHex,
              color: darkOrLightColor(colorResultHex),
            }}
            onClick={() => copyToClipboard(colorResultHex)}
            aria-label={`${texts.copyResult}: ${colorResultHex}`}
          >
            <span className={result_hex}>{colorResultHex}</span>
            <span>
              {100 - mixAmount}% + {mixAmount}% · {mixMode.toUpperCase()}
            </span>
          </button>

          <div className={color_formats_list}>
            {colorFormats.map(format => (
              <button
                type="button"
                className={format_button}
                key={format.label}
                onClick={() => copyToClipboard(format.value)}
                aria-label={`${texts.copyFormat} ${format.label}: ${format.value}`}
              >
                <span className={format_label}>{format.label}</span>
                <span className={format_value}>{format.value}</span>
              </button>
            ))}
          </div>

          <div className={result_actions}>
            <button
              type="button"
              className={primary_action}
              onClick={() => copyToClipboard(colorResultHex)}
            >
              {texts.copyResult}
            </button>
            <button type="button" className={action_btn} onClick={downloadMix}>
              {texts.download}
            </button>
          </div>
        </section>
      </div>

      <section className={scale_section} aria-labelledby="mix-scale-title">
        <h2 className={panel_heading} id="mix-scale-title">
          {texts.colorScale}
        </h2>
        <div className={gradient_bar} style={{ background: gradientCSS }} />
        <div className={scale_preview}>
          {colorScale.map((color, index) => {
            const normalizedColor = color.toUpperCase();
            return (
              <button
                type="button"
                key={`${normalizedColor}-${index}`}
                className={scale_color}
                style={{
                  backgroundColor: normalizedColor,
                  color: darkOrLightColor(normalizedColor),
                }}
                onClick={() => copyToClipboard(normalizedColor)}
                aria-label={`${texts.copyFormat} HEX: ${normalizedColor}`}
              >
                {normalizedColor}
              </button>
            );
          })}
        </div>
        <div className={control_actions}>
          <button
            type="button"
            className={action_btn}
            onClick={() =>
              copyToClipboard(
                colorScale.map(color => color.toUpperCase()).join(", ")
              )
            }
          >
            {texts.copyScale}
          </button>
          <button
            type="button"
            className={action_btn}
            onClick={copyCSSVariables}
          >
            {texts.copyCSS}
          </button>
        </div>
      </section>

      {toastMessage ? (
        <div className={toast} role="status">
          {toastMessage}
        </div>
      ) : null}
    </section>
  );
}

export default MixColors;
